const DEFAULT_TIMING_TIP =
  'Use 60-90 seconds for routine questions. If you are still choosing an approach after 2 minutes, mark it and move on.';

const DEFAULT_STRATEGY_TIP =
  'Before calculating, identify the question type: compare, solve, estimate, or count. The format tells you how exact the work must be.';

const LESSON_STRATEGY_TIPS = {
  'arithmetic-foundations': {
    timingTip:
      'Spend time slowly on setup while learning, then aim for 60-75 seconds on basic arithmetic during practice.',
    strategyTip:
      'Use mental math for small numbers, but write down sign changes, fractions, and percent conversions.',
    section:
      'The first Quant goal is accuracy, not speed. Use untimed practice until operations feel stable, then move to 90-second pacing.',
  },
  'math-translation': {
    timingTip:
      'Use the first 20-30 seconds to define variables and units. Bad setup wastes more time than slow algebra.',
    strategyTip:
      'Underline comparison words: more than, less than, twice, total, remaining, and per. Translate them before solving.',
  },
  'math-shortcuts': {
    timingTip:
      'Shortcuts should save time only after the setup is clear. If a shortcut feels risky, do the standard method.',
    strategyTip:
      'Estimate first, then calculate only as much as the answer choices require.',
  },
  integers: {
    timingTip:
      'Number property questions reward pattern recognition. Try divisibility, parity, and prime factors before brute force.',
    strategyTip:
      'For QC, test small legal integers, including 0, 1, primes, and negatives when allowed.',
  },
  'percentage-ratio': {
    timingTip:
      'Percent and ratio questions should usually be set up within 45 seconds; save exact arithmetic for the last step.',
    strategyTip:
      'Use part/whole labels and estimate percent changes before touching the calculator.',
  },
  exponents: {
    timingTip:
      'If bases can be matched, avoid decimals and calculators. Same-base rewriting is usually fastest.',
    strategyTip:
      'Convert roots and fractional exponents into the same notation before simplifying.',
  },
  algebra: {
    timingTip:
      'If algebra is growing after 90 seconds, check whether plugging in or factoring gives a shorter path.',
    strategyTip:
      'For QC and answer choices, test values before expanding expressions.',
  },
  sequences: {
    timingTip:
      'Identify the pattern in the first 30 seconds: add, multiply, alternate, or recursive.',
    strategyTip:
      'Write the position number beside each term so you do not confuse term value with term index.',
  },
  'absolute-value': {
    timingTip:
      'Absolute value inequalities are fast if you choose the right split. Do not start expanding until you know inside vs outside.',
    strategyTip:
      'Use the number line as a check for every interval answer.',
  },
  functions: {
    timingTip:
      'Function questions are often notation questions. Spend the first seconds identifying input, output, and composition order.',
    strategyTip:
      'Replace the input carefully before simplifying; for f(g(x)), compute the inside function first.',
  },
  sets: {
    timingTip:
      'Draw the Venn diagram immediately. A clear diagram is faster than holding all categories in memory.',
    strategyTip:
      'For min/max overlap, test the extreme cases: maximum overlap first, then minimum possible overlap.',
  },
  probabilities: {
    timingTip:
      'Decide whether order matters before calculating. This prevents most counting mistakes.',
    strategyTip:
      'Use complement probability when the direct count has many cases.',
  },
  'work-rate': {
    timingTip:
      'Set rate = work/time in the first 30 seconds. Combined-rate questions become simple once rates are aligned.',
    strategyTip:
      'Use total work = 1 unless the problem gives a more convenient total.',
  },
  'distance-rate': {
    timingTip:
      'Write distance = rate x time before calculating. Average-speed traps come from averaging rates too quickly.',
    strategyTip:
      'For round trips, use total distance divided by total time, not the average of the two speeds.',
  },
  'coordinate-geometry': {
    timingTip:
      'Sketch the axes quickly, even for algebraic coordinate questions.',
    strategyTip:
      'Slope, midpoint, and distance formulas are fastest when points are labeled before substitution.',
  },
  circles: {
    timingTip:
      'Circle questions are usually formula recognition. Identify radius, diameter, arc, or sector first.',
    strategyTip:
      'Leave pi symbolic unless the answer choices force a decimal.',
  },
  triangles: {
    timingTip:
      'Check for special right triangles and similarity before using long calculations.',
    strategyTip:
      'Mark equal angles and proportional sides directly on the diagram.',
  },
  quadrilaterals: {
    timingTip:
      'Classify the shape before choosing a formula; rectangles, squares, rhombi, and trapezoids use different shortcuts.',
    strategyTip:
      'Split unfamiliar quadrilaterals into triangles or rectangles.',
  },
  polygons: {
    timingTip:
      'Use the polygon formula only after confirming whether the polygon is regular.',
    strategyTip:
      'For interior angles, remember the total sum first, then divide only if regular.',
  },
  'three-dimensional-figures': {
    timingTip:
      'For 3D figures, identify whether the question asks length, area, surface area, or volume.',
    strategyTip:
      'Draw or label the base separately; many 3D problems reduce to a 2D base problem.',
  },
  volumes: {
    timingTip:
      'Volume questions are fast when you identify the base area first.',
    strategyTip:
      'For composite solids, add or subtract simpler volumes instead of searching for a new formula.',
  },
  statistics: {
    timingTip:
      'Do not calculate every statistic unless asked. Identify mean, median, range, or spread first.',
    strategyTip:
      'For weighted averages, use total sum divided by total count.',
  },
  graphs: {
    timingTip:
      'Spend the first 20 seconds reading labels, units, and axes before answering.',
    strategyTip:
      'For data interpretation, quote the chart values first, then do arithmetic.',
  },
};

export const applyLessonStrategyTips = (lessonId, sections = []) => {
  const tips = LESSON_STRATEGY_TIPS[lessonId] || {
    timingTip: DEFAULT_TIMING_TIP,
    strategyTip: DEFAULT_STRATEGY_TIP,
  };

  return sections.map((section) => ({
    ...section,
    content: {
      ...section.content,
      concepts: (section.content?.concepts || []).map((concept) => ({
        ...concept,
        timingTip: concept.timingTip || tips.timingTip,
        strategyTip: concept.strategyTip || tips.strategyTip,
      })),
    },
  }));
};
